import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, combineLatest, filter } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { ClausesSearchCriterionsFactory } from 'src/app/core/factories/clauses-search-criterions.factory';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { Criterion, SearchItemModel } from 'src/app/core/services/base-event-service';
import {
  ClientSideSearchService,
  FilterRequestItem,
} from 'src/app/core/services/client-side-search.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { selectDefaultContentLanguageId, selectUser } from 'src/app/core/store/selectors';
import { AppState } from 'src/app/core/store/state';
import { areGenerateClausePayloadsEqual } from 'src/app/core/utils/doc-generation.utils';
import { isPresent } from 'src/app/core/utils/isPresent';
import { getClausesFilterConditionsFromModel } from 'src/app/core/utils/clauses.utils';
import {
  DocumentStructureItem,
  GenerateClauseListPayload,
  GenerateClauseListResponse,
} from 'src/app/modules/document-generation/services/document-generation-api.service';
import { DocumentGenerationActions } from 'src/app/modules/document-generation/store/actions';
import {
  selectCloseGenerateDocumentModal,
  selectGenerateClauseListFailed,
  selectGenerateClauseListResponse,
  selectGeneratingClauseList,
} from 'src/app/modules/document-generation/store/selectors';
import { UserDto } from 'src/app/modules/login/services/user-api.service';
import { ClausesListEventsService } from 'src/app/modules/clauses/components/clauses-list/clauses-list-events.service';
import { GetClauseDto } from 'src/app/modules/clauses/models/clauses.model';
import { selectClauses } from 'src/app/modules/clauses/store/selectors';
import { DocTypePipe } from 'src/app/shared/pipes/doc-type.pipe';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { PositionDto } from 'src/app/shared/services/positions-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';

@UntilDestroy()
@Component({
  selector: 'app-doc-create-modal',
  templateUrl: './doc-create-modal.component.html',
  styleUrl: './doc-create-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocCreateModalComponent implements OnInit, AfterViewInit {
  public formStep1: FormGroup | undefined;
  public title: string = this.translocoService.translate(`titles.createDocument`);
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public constants = constants;
  public currentStep: DocCreateModalSteps = DocCreateModalSteps.Step1;
  public DocCreateModalSteps = DocCreateModalSteps;

  public docTypeItems: any;
  public positionItems!: PositionDto[];

  public user$: Observable<UserDto | null> | undefined;
  public isGeneratingClauseList$ = this.store$.select(selectGeneratingClauseList);
  public generateClauseListFailed: boolean = false;
  public currentUser: UserDto | null | undefined;
  public contentLanguages: LanguageDto[] = [];
  public clauses: GetClauseDto[] = [];
  public clausesCriterions: Criterion[] =
    this.clausesSearchCriterionsFactory.getAllItemsAsArray();
  public noClausesFound: boolean = false;
  public docTypeNameTranslated: string = '';
  public languageTranslated: string = '';
  public positionTranslated: string = '';
  public dataFiltered: GetClauseDto[] = [];
  public documentGenerationTrackingId: string | undefined;
  public documentName: string | undefined;
  public status: number | undefined;
  public generateClauseListResponse: GenerateClauseListResponse | null = null;
  public canWrite = false;
  private selectedDocumentStructureOrders = new Set<number>();
  private previousGenerateClausePayload: GenerateClauseListPayload | null = null;
  private previousFilteredClauses: GetClauseDto[] = [];
  public selectedDocumentStructureItems: DocumentStructureItem[] = [];

  constructor(
    private store$: Store<AppState>,
    private bsModalRef: BsModalRef,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly docTypePipe: DocTypePipe,
    private clausesSearchCriterionsFactory: ClausesSearchCriterionsFactory,
    private clausesListEventsService: ClausesListEventsService,
    private clientSideSearchService: ClientSideSearchService,
    private readonly permissionService: PermissionService,
  ) {}

  public ngOnInit(): void {
    this.clausesListEventsService.setFilterCriterions(this.clausesCriterions);
    this.initForm();

    combineLatest([
      this.permissionService.canWrite$(PermissionResource.Documents),
      this.permissionService.canWrite$(PermissionResource.DocumentGeneration),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([canWriteDocuments, canWriteDocumentGeneration]) => {
        this.canWrite = canWriteDocuments && canWriteDocumentGeneration;
        this.syncFormAccess();
      });

    this.store$
      .select(selectGenerateClauseListResponse)
      .pipe(untilDestroyed(this))
      .subscribe((generateClauseListResponse: GenerateClauseListResponse | null) => {
        this.generateClauseListResponse = generateClauseListResponse;
        this.resetAndSelectAllDocumentStructure();
        this.cdr.markForCheck();
      });

    this.store$
      .select(selectGenerateClauseListFailed)
      .pipe(untilDestroyed(this))
      .subscribe((generateClauseListFailed: boolean) => {
        this.generateClauseListFailed = generateClauseListFailed;
        this.cdr.markForCheck();
      });

    this.resetAndSelectAllDocumentStructure();
  }

  ngAfterViewInit(): void {
    this.store$
      .select(selectCloseGenerateDocumentModal)
      .pipe(untilDestroyed(this))
      .subscribe((close) => {
        if (close) {
          this.bsModalRef.hide();
          this.store$.dispatch(DocumentGenerationActions.clearGenerateDocumentCancelled());
        }
      });
  }

  initSubscriptions() {
    this.formStep1?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.setTranslatedValues();

      if (this.noClausesFound === true) {
        this.noClausesFound = false;
      }

      this.cdr.markForCheck();
    });
  }

  initForm() {
    combineLatest([
      this.store$.select(selectUser),
      this.store$.select(selectDefaultContentLanguageId),
      this.store$.select(selectClauses).pipe(filter(isPresent)),
    ])
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe(([user, defaultContentLanguageId, clauses]) => {
        this.currentUser = user;
        this.clauses = clauses || [];

        setTimeout(() => {
          this.formStep1 = this.fb.group({
            documentType: ['', { validators: [Validators.required] }],
            languageId: [defaultContentLanguageId, { validators: [Validators.required] }],
            myPosition: ['', { validators: [Validators.required] }],
          });

          this.initSubscriptions();
          this.syncFormAccess();
          this.cdr.markForCheck();
        });
      });
  }

  public setTranslatedValues(): void {
    const docTypeId = this.formStep1?.get('documentType')?.value;
    const languageId = this.formStep1?.get('languageId')?.value;
    const positionId = this.formStep1?.get('myPosition')?.value;

    if (docTypeId) {
      this.docTypePipe.transform(docTypeId).subscribe((value) => {
        this.docTypeNameTranslated = value;
        this.cdr.detectChanges();
      });
    }

    if (languageId) {
      this.languageTranslated =
        this.contentLanguages.find((lang) => lang.id === languageId)?.displayName || '';
      this.cdr.detectChanges();
    }

    if (positionId) {
      this.positionTranslated = this.translocoService.translate(`positions.${positionId}`);
      this.cdr.detectChanges();
    }
  }

  public generateOnFirstStep(): void {
    if (!this.canWrite) {
      return;
    }

    this.getFilteredClauses();

    if (this.dataFiltered.length === 0) {
      this.noClausesFound = true;
    } else {
      this.noClausesFound = false;

      const payload = this.getGenerateClausePayload();

      this.store$.dispatch(
        DocumentGenerationActions.generateClauseList({
          payload,
          createDocumentImmediately: true,
        }),
      );
    }
  }

  getGenerateClausePayload(): GenerateClauseListPayload {
    return {
      documentTypeId: this.formStep1?.get('documentType')?.value,
      languageId: this.formStep1?.get('languageId')?.value,
      positionId: this.formStep1?.get('myPosition')?.value,
    };
  }

  getFilteredClauses(): GetClauseDto[] {
    const payload = this.getGenerateClausePayload();

    if (areGenerateClausePayloadsEqual(payload, this.previousGenerateClausePayload)) {
      this.dataFiltered = [...this.previousFilteredClauses];
      return this.dataFiltered;
    }

    let conditions = getClausesFilterConditionsFromModel(
      {
        documentType: [this.formStep1?.get('documentType')?.value],
        languageId: [this.formStep1?.get('languageId')?.value],
        position: [this.formStep1?.get('myPosition')?.value],
      },
      this.clausesSearchCriterionsFactory,
    );

    let filterItems = conditions.map((item: SearchItemModel) => {
      item.searchValue = item.searchValue?.toString();
      return item;
    });

    this.dataFiltered = Object.assign(this.clauses);

    filterItems.forEach((filterItem) => {
      const criterion = this.clausesListEventsService
        ?.getFilterCriterions()
        .find((filterCriterion: Criterion) => filterItem.criterion === filterCriterion.id);

      this.dataFiltered = this.clientSideSearchService.doFilterItems(
        {
          propertyNames: criterion?.data || [],
          propertyValue: filterItem.searchValue,
          operator: filterItem.operator,
        } as FilterRequestItem,
        this.dataFiltered,
        criterion?.type || 1,
      );
    });

    this.previousGenerateClausePayload = payload;
    this.previousFilteredClauses = [...this.dataFiltered];

    return this.dataFiltered;
  }

  public previewAndCustomize(): void {
    if (!this.canWrite) {
      return;
    }

    this.getFilteredClauses();

    if (this.dataFiltered.length === 0) {
      this.noClausesFound = true;
    } else {
      this.noClausesFound = false;
      this.currentStep = DocCreateModalSteps.Step2;
      const payload = this.getGenerateClausePayload();

      this.store$.dispatch(
        DocumentGenerationActions.generateClauseList({
          payload,
          createDocumentImmediately: false,
        }),
      );
    }
  }

  public back(): void {
    this.currentStep = DocCreateModalSteps.Step1;
  }

  public createOnStep2(): void {
    if (!this.canWrite || !this.generateClauseListResponse) {
      return;
    }

    this.store$.dispatch(
      DocumentGenerationActions.generateDocument({
        generateClauseListResponse: {
          ...this.generateClauseListResponse,
          documentStructure: this.selectedDocumentStructureItems,
        },
      }),
    );
  }

  public onCancel(): void {
    this.bsModalRef.hide();
  }

  public isDocumentStructureSelected(item: DocumentStructureItem): boolean {
    return this.selectedDocumentStructureOrders.has(item.order);
  }

  public toggleSelectAllDocumentStructure(checked: boolean | null | undefined): void {
    if (!this.canWrite) {
      return;
    }

    const shouldSelect = !!checked;
    this.selectedDocumentStructureOrders.clear();

    if (shouldSelect) {
      this.documentStructureItems.forEach((item) =>
        this.selectedDocumentStructureOrders.add(item.order),
      );
    }

    this.syncSelectedDocumentStructureItems();
    this.cdr.markForCheck();
  }

  public toggleDocumentStructureItem(
    item: DocumentStructureItem,
    checked: boolean | null | undefined,
  ): void {
    if (!this.canWrite) {
      return;
    }

    if (!!checked) {
      this.selectedDocumentStructureOrders.add(item.order);
    } else {
      this.selectedDocumentStructureOrders.delete(item.order);
    }

    this.syncSelectedDocumentStructureItems();
    this.cdr.markForCheck();
  }

  private resetAndSelectAllDocumentStructure(): void {
    this.selectedDocumentStructureOrders.clear();
    this.documentStructureItems.forEach((item) =>
      this.selectedDocumentStructureOrders.add(item.order),
    );
    this.syncSelectedDocumentStructureItems();
  }

  private syncSelectedDocumentStructureItems(): void {
    this.selectedDocumentStructureItems = this.documentStructureItems.filter((item) =>
      this.selectedDocumentStructureOrders.has(item.order),
    );
  }

  public retryGenerateClauseList(): void {
    if (!this.canWrite) {
      return;
    }

    this.store$.dispatch(DocumentGenerationActions.retryGenerateClauseList());
  }

  private syncFormAccess(): void {
    if (!this.formStep1) {
      return;
    }

    if (this.canWrite) {
      this.formStep1.enable({ emitEvent: false });
      return;
    }

    this.formStep1.disable({ emitEvent: false });
  }

  get documentStructureData(): DocumentStructureItem[] {
    return this.generateClauseListResponse?.documentStructure || [];
  }

  get documentStructureItems(): DocumentStructureItem[] {
    return this.generateClauseListResponse?.documentStructure || [];
  }

  get selectedDocumentStructureCount(): number {
    return this.selectedDocumentStructureItems.length;
  }

  get isAllDocumentStructureSelected(): boolean {
    const itemsCount = this.documentStructureItems.length;
    return itemsCount > 0 && this.selectedDocumentStructureOrders.size === itemsCount;
  }

  get isDocumentStructureSelectionIndeterminate(): boolean {
    const itemsCount = this.documentStructureItems.length;

    return (
      itemsCount > 0 &&
      this.selectedDocumentStructureOrders.size > 0 &&
      this.selectedDocumentStructureOrders.size < itemsCount
    );
  }
}

export enum DocCreateModalSteps {
  Step1 = 1,
  Step2 = 2,
}
